import React from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationsContext';
import { routeForNotification } from '../../../utils/notificationRoute';
import DashboardFooter from '../components/DashboardFooter';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsHub() {
  const { items, unread, loading, nextBefore, markRead, markAllRead, remove, loadMore } = useNotifications();
  const navigate = useNavigate();

  const handleClick = (n) => {
    if (!n.read_at) markRead(n.id);
    const dest = routeForNotification(n);
    if (/^https?:\/\//i.test(dest)) window.open(dest, '_blank', 'noopener,noreferrer');
    else navigate(dest);
  };

  return (
    <div className="h-full overflow-y-auto bg-white flex flex-col">
      <div className="flex-1 w-full max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <FiBell className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-black">Notifications</h1>
              <p className="text-gray-500 text-sm">{unread > 0 ? `${unread} unread` : 'All caught up'}</p>
            </div>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-sm text-blue-600 hover:underline">
              Mark all read
            </button>
          )}
        </div>

        {loading && items.length === 0 ? (
          <p className="text-gray-400">Loading…</p>
        ) : items.length === 0 ? (
          <div className="border border-gray-200 rounded-xl p-10 text-center text-gray-400">
            No notifications yet.
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {items.map((n) => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${n.read_at ? '' : 'bg-blue-50/40'}`}
              >
                <div className="flex items-start gap-3">
                  {!n.read_at && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-black">{n.title}</p>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    {n.body && <p className="text-sm text-gray-600 mt-0.5">{n.body}</p>}
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-gray-400">{n.type}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                    className="flex-shrink-0 px-2 text-xl leading-none text-gray-300 hover:text-red-500"
                    aria-label="Delete notification"
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {nextBefore && (
          <button
            onClick={loadMore}
            className="mt-4 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Load more
          </button>
        )}
      </div>
      <DashboardFooter />
    </div>
  );
}
